import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";

const rid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const cls = "bg-[#0C121E] border-slate-800/80 h-10 text-sm";

function Picker({ label, items, value, onPick, render, testid, newFields, onCreate, snapshot }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});
  const known = value && items.some((x) => x.id === value);
  const selValue = known ? value : snapshot ? "__snap" : "none";
  const create = async () => {
    if (!(draft[newFields[0].key] || "").trim()) return;
    const ok = await onCreate({ id: rid(), ...Object.fromEntries(newFields.map((f) => [f.key, (draft[f.key] || "").trim()])) });
    if (ok) { setAdding(false); setDraft({}); }
  };
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
      <Select value={selValue} onValueChange={(v) => (v === "__snap" ? null : v === "__new" ? setAdding(true) : onPick(v === "none" ? null : items.find((x) => x.id === v)))}>
        <SelectTrigger data-testid={testid} className={cls}><SelectValue placeholder="Non indicato" /></SelectTrigger>
        <SelectContent className="bg-slate-950 border-slate-800 max-h-72">
          <SelectItem value="none">Non indicato</SelectItem>
          {!known && snapshot && <SelectItem value="__snap">{snapshot}</SelectItem>}
          {items.map((x) => <SelectItem key={x.id} value={x.id}>{render(x)}</SelectItem>)}
          <SelectItem value="__new">+ Nuovo…</SelectItem>
        </SelectContent>
      </Select>
      {adding && (
        <div className="flex flex-col sm:flex-row gap-2" data-testid={`${testid}-new`}>
          {newFields.map((f) => <Input key={f.key} placeholder={f.label} value={draft[f.key] || ""} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} className={cls} data-testid={`${testid}-new-${f.key}`} />)}
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={create} className="flex-1 rounded-lg px-3 h-10 text-xs font-semibold border border-amber-500/50 text-amber-200 hover:bg-amber-500/10" data-testid={`${testid}-new-save`}>Aggiungi</button>
            <button type="button" onClick={() => { setAdding(false); setDraft({}); }} className="rounded-lg px-3 h-10 text-xs border border-slate-700 text-slate-400" data-testid={`${testid}-new-cancel`}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Autista e veicolo del fornitore per la prenotazione: snapshot sulla prenotazione + archivio del fornitore.
export function SupplierExecFields({ supplier, f, setF, onSupplierUpdated }) {
  const drivers = supplier?.drivers || [];
  const vehicles = supplier?.vehicles || [];
  const pickDriver = (d) => setF((s) => ({ ...s, partner_driver_id: d?.id || null, partner_driver_name: d?.name || null, partner_driver_phone: d?.phone || null }));
  const pickVehicle = (v) => setF((s) => ({ ...s, partner_vehicle_id: v?.id || null, partner_vehicle_name: v?.model || null, partner_vehicle_plate: v?.plate || null }));
  const addTo = (key, pick) => async (item) => {
    try {
      const norm = key === "vehicles" ? { ...item, plate: (item.plate || "").toUpperCase() } : item;
      const r = await api.put(`/suppliers/${supplier.id}/fleet`, { drivers: supplier.drivers || [], vehicles: supplier.vehicles || [], [key]: [...(supplier[key] || []), norm] });
      onSupplierUpdated(r.data);
      pick(norm);
      toast.success("Aggiunto all'archivio del fornitore");
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
  };
  if (!supplier) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1" data-testid="booking-supplier-exec">
      <Picker label="Autista esterno" items={drivers} value={f.partner_driver_id} onPick={pickDriver} testid="booking-partner-driver" snapshot={f.partner_driver_name ? `${f.partner_driver_name}${f.partner_driver_phone ? ` · ${f.partner_driver_phone}` : ""}` : null}
        render={(d) => `${d.name}${d.phone ? ` · ${d.phone}` : ""}`} newFields={[{ key: "name", label: "Nome e cognome" }, { key: "phone", label: "Telefono" }]} onCreate={addTo("drivers", pickDriver)} />
      <Picker label="Veicolo esterno" items={vehicles} value={f.partner_vehicle_id} onPick={pickVehicle} testid="booking-partner-vehicle" snapshot={f.partner_vehicle_name || f.partner_vehicle_plate ? [f.partner_vehicle_name, f.partner_vehicle_plate].filter(Boolean).join(" · ") : null}
        render={(v) => `${v.model}${v.plate ? ` · ${v.plate}` : ""}`} newFields={[{ key: "model", label: "Modello" }, { key: "plate", label: "Targa" }]} onCreate={addTo("vehicles", pickVehicle)} />
    </div>
  );
}
